import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Trash2, Search, FilterX, Shield, User as UserIcon } from "lucide-react";
import { formatDateUK } from "@/utils/dateUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_LABEL = {
  create: "Created",
  update: "Updated",
  delete: "Deleted",
};
const ACTION_TONE = {
  create: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  update: "bg-amber-50 text-amber-800 border border-amber-200",
  delete: "bg-rose-50 text-rose-700 border border-rose-200",
};

function fmtTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AuditLogModal({ open, onClose, competitionId, competitionName, competitionEndDate, initialPlayerId, initialPlayerName }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [filters, setFilters] = useState({
    q: "",
    actor: "",          // "" | player | admin | self | other
    entity_type: "",    // "" | round | registration
    action: "",         // "" | create | update | delete
    date_from: "",
    date_to: "",
  });

  const fetchLogs = useCallback(async (opts = {}) => {
    if (!competitionId) return;
    const { showToastOnEmpty = false, overrideFilters } = opts;
    setLoading(true);
    try {
      const active = overrideFilters || filters;
      const params = {};
      Object.entries(active).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const res = await axios.get(`${API}/admin/competitions/${competitionId}/audit-logs`, {
        params,
        withCredentials: true,
      });
      const list = res.data?.logs || [];
      setLogs(list);
      setHasSearched(true);
      if (showToastOnEmpty && list.length === 0) {
        toast.info("No changes match your filters", {
          description: "Try clearing filters or widening the date range.",
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [competitionId, filters]);

  // Keep a stable ref so the open-effect can call the latest fetchLogs
  // without re-firing every time filters change.
  const fetchLogsRef = useRef(fetchLogs);
  useEffect(() => { fetchLogsRef.current = fetchLogs; }, [fetchLogs]);

  // Initial load when modal opens — show ALL entries by ignoring any stale filter state.
  useEffect(() => {
    if (open) {
      setHasSearched(false);
      // When opened from a per-player shortcut on the admin comp view, we
      // pre-filter to that player so the admin sees just their activity.
      const empty = {
        q: "",
        actor: "",
        entity_type: "",
        action: "",
        date_from: "",
        date_to: "",
        player_id: initialPlayerId || "",
      };
      setFilters(empty);
      fetchLogsRef.current({ overrideFilters: empty });
    }
  }, [open, competitionId, initialPlayerId]);

  const clearFilters = () => {
    setFilters({ q: "", actor: "", entity_type: "", action: "", date_from: "", date_to: "" });
  };

  const handleExport = () => {
    // Bypass axios/blob entirely — the server already sends
    //   Content-Disposition: attachment; filename="..."
    // so navigating an invisible anchor to the URL triggers the browser's
    // native download. This works reliably from inside a Radix Dialog,
    // where synthetic .click() on a programmatic <a> can be swallowed by
    // the dialog's focus trap.
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.append(k, v); });
    const href = `${API}/admin/competitions/${competitionId}/audit-logs/export${qs.toString() ? `?${qs.toString()}` : ""}`;

    const link = document.createElement("a");
    link.href = href;
    // No target="_blank" — same-tab download keeps the dialog open and
    // the browser still routes Content-Disposition: attachment to disk.
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success("Audit log exported");
  };

  const isCompetitionEnded = (() => {
    if (!competitionEndDate) return false;
    try {
      // Compare date-only in local TZ; backend re-validates strictly anyway
      const end = new Date(`${competitionEndDate}T23:59:59`);
      return new Date() > end;
    } catch {
      return false;
    }
  })();

  const handlePurge = async () => {
    if (!window.confirm(
      "Permanently delete ALL audit-log entries for this competition?\n\n" +
      "This cannot be undone. Only do this once you are certain there are no pending disputes."
    )) return;
    try {
      const res = await axios.delete(`${API}/admin/competitions/${competitionId}/audit-logs`, {
        withCredentials: true,
      });
      toast.success(`Deleted ${res.data?.deleted_count ?? 0} audit entries`);
      setLogs([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to purge audit logs");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid="audit-log-modal"
        className="max-w-6xl w-[95vw] max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-ink flex-wrap">
            <Shield className="w-5 h-5 text-accent-deep" />
            Audit Log – {competitionName}
            {initialPlayerName && filters.player_id && (
              <span
                data-testid="audit-pinned-player-chip"
                className="inline-flex items-center gap-1.5 text-xs font-medium bg-accent-tint text-ink border border-accent/30 rounded-full pl-2 pr-1 py-0.5"
              >
                <span>For: {initialPlayerName}</span>
                <button
                  onClick={() => {
                    const next = { ...filters, player_id: "" };
                    setFilters(next);
                    fetchLogs({ overrideFilters: next, showToastOnEmpty: false });
                  }}
                  data-testid="clear-pinned-player-btn"
                  className="ml-0.5 text-ink-muted hover:text-ink px-1"
                  title="Show all players' activity"
                >
                  ×
                </button>
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Every score and registration change made by players or admins is recorded here for review.
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 p-3 rounded-lg bg-app border border-line">
          <div className="md:col-span-2">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
              <Input
                data-testid="audit-search-input"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") fetchLogs({ showToastOnEmpty: true }); }}
                placeholder="Player name, email, or change…"
                className="pl-8"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">User Role</Label>
            <select
              data-testid="audit-filter-actor"
              value={filters.actor}
              onChange={(e) => setFilters((f) => ({ ...f, actor: e.target.value }))}
              className="w-full h-10 rounded-md border border-line bg-white px-2 text-sm"
            >
              <option value="">All</option>
              <option value="player">Players</option>
              <option value="admin">Admins</option>
              <option value="self">Self-edits (player editing own)</option>
              <option value="other">Admin on behalf of player</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Entity</Label>
            <select
              data-testid="audit-filter-entity"
              value={filters.entity_type}
              onChange={(e) => setFilters((f) => ({ ...f, entity_type: e.target.value }))}
              className="w-full h-10 rounded-md border border-line bg-white px-2 text-sm"
            >
              <option value="">All</option>
              <option value="round">Round</option>
              <option value="registration">Registration</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Action</Label>
            <select
              data-testid="audit-filter-action"
              value={filters.action}
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
              className="w-full h-10 rounded-md border border-line bg-white px-2 text-sm"
            >
              <option value="">All</option>
              <option value="create">Created</option>
              <option value="update">Updated</option>
              <option value="delete">Deleted</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input
              type="date"
              data-testid="audit-filter-date-from"
              value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            />
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs">To</Label>
            <Input
              type="date"
              data-testid="audit-filter-date-to"
              value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-2">
          <Button
            data-testid="audit-search-btn"
            onClick={() => fetchLogs({ showToastOnEmpty: true })}
            size="sm"
            disabled={loading}
            className="bg-accent-deep hover:bg-accent-deep flex items-center gap-2"
          >
            <Search className={`w-4 h-4 ${loading ? "animate-pulse" : ""}`} />
            {loading ? "Searching…" : "Search"}
          </Button>
          <Button
            data-testid="audit-clear-filters-btn"
            onClick={clearFilters}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <FilterX className="w-4 h-4" />
            Clear filters
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-ink-muted">{logs.length} entr{logs.length === 1 ? "y" : "ies"}</span>
            <Button
              data-testid="audit-export-btn"
              onClick={handleExport}
              size="sm"
              className="bg-accent-deep hover:bg-accent-deep flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Excel
            </Button>
            <Button
              data-testid="audit-purge-btn"
              onClick={handlePurge}
              variant="destructive"
              size="sm"
              disabled={!isCompetitionEnded}
              title={isCompetitionEnded
                ? "Permanently delete all audit log entries for this competition"
                : "Available once the competition end date has passed"}
              className="flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Purge logs
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-3 overflow-x-auto rounded-lg border border-line">
          <table className="min-w-full text-sm">
            <thead className="bg-app text-ink-muted">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Player</th>
                <th className="text-left px-3 py-2">Changed by</th>
                <th className="text-left px-3 py-2">Summary</th>
              </tr>
            </thead>
            <tbody data-testid="audit-log-table-body">
              {loading && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-ink-muted">Loading…</td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-ink-muted" data-testid="audit-empty-state">
                    {hasSearched
                      ? "No changes match your filters. Try clearing them or widening the date range."
                      : "No changes have been recorded yet for this competition."}
                  </td>
                </tr>
              )}
              {!loading && logs.map((log) => (
                <tr key={log.id} className="border-t border-line align-top">
                  <td className="px-3 py-2 whitespace-nowrap text-ink">{fmtTs(log.timestamp)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${ACTION_TONE[log.action] || "bg-gray-100 text-gray-700"}`}>
                      {ACTION_LABEL[log.action] || log.action}
                    </span>
                    <span className="ml-2 text-xs text-ink-muted">
                      {log.entity_type}
                      {/* Hide the 999 placeholder — pre-fix historical entries
                          may still carry it; the summary column already tells
                          the reader when the round was played. */}
                      {log.round_number && log.round_number < 900 ? ` #${log.round_number}` : ""}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-ink">{log.player_name || "—"}</div>
                    {log.player_email && <div className="text-xs text-ink-muted">{log.player_email}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      {log.actor_role === "admin"
                        ? <Shield className="w-3.5 h-3.5 text-accent-deep" />
                        : <UserIcon className="w-3.5 h-3.5 text-ink-muted" />}
                      <span className="font-medium text-ink">{log.actor_name || "—"}</span>
                      {log.actor_is_self
                        ? <span className="text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-1 rounded">self</span>
                        : log.actor_role === "admin"
                          ? <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1 rounded">admin</span>
                          : null}
                    </div>
                    {log.actor_email && <div className="text-xs text-ink-muted">{log.actor_email}</div>}
                  </td>
                  <td className="px-3 py-2 text-ink">
                    <div>{log.summary || "—"}</div>
                    {(log.changes || []).length > 1 && (
                      <details className="mt-1">
                        <summary className="text-xs text-ink-muted cursor-pointer">All field changes ({log.changes.length})</summary>
                        <ul className="mt-1 text-xs text-ink-muted list-disc list-inside">
                          {log.changes.map((c, i) => (
                            <li key={i}>
                              <span className="font-medium text-ink">{c.display || c.field}:</span>{" "}
                              <span>{String(c.from ?? "—")} → {String(c.to ?? "—")}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!isCompetitionEnded && (
          <p className="mt-2 text-xs text-ink-muted">
            Purge is available after {competitionEndDate ? formatDateUK(competitionEndDate) : "the competition ends"} to keep an immutable record during live play.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
